<template>
    <v-form 
        v-model="valid" 
        ref="form" 
        :disabled="props.disabled" 
        :readonly="resolvedReadonlyFields" 
        :loading="resolvedIsFetchingAssessmentActivationStatus"
        :style="props.standalone ? 'max-width: 960px; margin-inline: auto' : ''"
    >
        <v-alert
            v-if="standaloneAlert || standaloneAlert?.title || standaloneAlert?.message || standaloneAlert?.text"
            :title="standaloneAlert?.title ?? 'Sucesso'"
            :text="standaloneAlert?.text ?? standaloneAlert?.message"
            :color="standaloneAlert?.color ?? 'success'"
            :variant="standaloneAlert?.variant ?? 'tonal'"
            :type="standaloneAlert?.type ?? 'success'"
        ></v-alert>
        <v-row class="ma-0">
            <v-col class="" cols="12">

                <v-radio-group 
                    v-model="formValues.type"
                    :label="`Tipo de Avaliação${resolvedReadonlyFields ? '' : '*'}`"
                    :rules="[
                        formValidationRules.requiredIf(
                            formValues.type,
                            !resolvedReadonlyFields
                        )
                    ]"    
                    :readonly="resolvedReadonlyFields || !!props?.activationStatusModel?.type"
                    :disabled="props.disabled"
                >
                    <v-radio value="self" label="Auto-Avaliação"></v-radio>
                    <v-radio value="ascending" label="Avaliação Ascendente"></v-radio>
                    <v-radio value="downward" label="Avaliação Descendente"></v-radio>
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row class="ma-0">
            <v-col class="" cols="12">
                <v-autocomplete 
                    v-model="formValues.year" 
                    :items="yearsList" 
                    :label="`Ano${resolvedReadonlyFields ? '' : '*'}`" 
                    autocomplete="off"
                    variant="outlined"
                    :rules="[
                        formValidationRules.requiredIf(
                            formValues.year,
                            !resolvedReadonlyFields
                        )
                    ]" 
                    :clearable="!resolvedReadonlyFields  
                        && !props?.activationStatusModel?.year" 
                    :readonly="resolvedReadonlyFields  
                        || !!props?.activationStatusModel?.year" 
                    :menu-icon="resolvedReadonlyFields  
                        || !!props?.activationStatusModel?.year ? '' : undefined"
                    :disabled="
                        props.disabled
                        || resolvedIsFetchingAssessmentActivationStatus
                        || resolvedIsFetchingYearsList
                    "
                    :loading="
                        resolvedIsFetchingAssessmentActivationStatus
                        || resolvedIsFetchingYearsList
                    "
                    
                />
            </v-col>
        </v-row>
        <v-row class="ma-0">
            <v-col class=" d-flex flex-column align-start" cols="12">
                <p>Avaliação Ativa{{resolvedReadonlyFields ? '' : "*"}}</p>
                <v-switch 
                    v-model="formValues.enabled" 
                    :readonly="resolvedReadonlyFields || ![null, undefined].includes(props.activationStatusModel?.enabled)"
                    :indeterminate="formValues.enabled === null"
                    :disabled="
                        props.disabled
                        
                    "
                    :class="
                        [null, undefined].includes(props.activationStatusModel?.enabled)
                        ? 'cursor-pointer'
                        : 'cursor-default'
                    "
                    :loading="resolvedIsFetchingAssessmentActivationStatus"
                    :rules="[
                        formValidationRules.requiredIf(
                            formValues.enabled,
                            !resolvedReadonlyFields
                        )
                    ]"
                    
                >
                    <template #prepend>
                        <span 
                            :class="
                                [null, undefined].includes(props.activationStatusModel?.enabled)
                                ? 'cursor-pointer'
                                : 'cursor-default'
                            "
                            @click="
                                [null, undefined].includes(props.activationStatusModel?.enabled)
                                ? (formValues.enabled = false)
                                : null
                            "
                        >
                            Não
                        </span>
                    </template>
                    <template #append>
                        <span 
                            :class="
                                [null, undefined].includes(props.activationStatusModel?.enabled)
                                ? 'cursor-pointer'
                                : 'cursor-default'
                            " 
                            @click="
                                [null, undefined].includes(props.activationStatusModel?.enabled)
                                ? (formValues.enabled = true)
                                : null
                            ">Sim</span>
                    </template>
                </v-switch>
            </v-col>
        </v-row>
        <v-row class="ma-0">
            <v-col class="" cols="12">
                <v-date-input
                    v-model="formValues.starts_on"
                    :label="`De${resolvedReadonlyFields ? '' : '*'}:`"
                    :clearable="!resolvedReadonlyFields"
                    :readonly="resolvedReadonlyFields"
                    :disabled="props.disabled"
                    :loading="resolvedIsFetchingAssessmentActivationStatus"
                    prepend-icon=""
                    variant="outlined"
                    :rules="[
                        formValidationRules.requiredIfAll(
                            formValues.starts_on,
                            [
                                !resolvedReadonlyFields,
                                formValues.enabled === true
                            ]
                        )
                    ]"
                    :max="getDate(formValues.ends_on)"
                >

                </v-date-input>
            </v-col>
        </v-row>
        <v-row class="ma-0">
            <v-col class="" cols="12">
                <v-date-input 
                    v-model="formValues.ends_on" 
                    :label="`Até${resolvedReadonlyFields ? '' : '*'}:`"
                    :clearable="!resolvedReadonlyFields"
                    :readonly="resolvedReadonlyFields"
                    :disabled="props.disabled"
                    :loading="resolvedIsFetchingAssessmentActivationStatus"
                    prepend-icon=""
                    variant="outlined"
                    :rules="[
                        formValidationRules.requiredIfAll(
                            formValues.ends_on,
                            [
                                !resolvedReadonlyFields,
                                formValues.enabled !== null
                            ]
                        )
                    ]"
                    :min="getDate(formValues.starts_on)"
                ></v-date-input>
            </v-col>
        </v-row>
        <v-row class="ma-0" v-if="props.standalone">
            <v-alert 
                v-if="submitError || submitError?.title || submitError?.message || submitError?.text"
                :variant="submitError?.variant ?? 'tonal'"
                :color="submitError?.color ?? 'error'"
                :title="submitError?.title ?? 'Erro'"
                :text="submitError?.text ?? submitError?.message ?? String(submitError)"
                style="white-space: pre;"
            >
            </v-alert>
            <v-col 
                v-if="actionReferences.isValidSubmitAction(localAction)"
                cols="12"
                class=" d-flex justify-end" 
            >
                <v-btn
                    class="text-none"
                    :prepend-icon="resolvedSubmitButtonIcon"
                    :color="resolvedSubmitButtonColor"
                    :disabled="
                        resolvedIsSubmitCallPending 
                        || resolvedIsFetchingAssessmentActivationStatus 
                        || (typeof formValues.enabled !== 'boolean')
                    "
                    @click="submit"
                >
                    {{ resolvedSubmitButtonLabel}}
                </v-btn>
            </v-col>
        </v-row>
    </v-form>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import actionReferences from '../../../composables/actionReferences';
import formValidationRules from '../../../composables/formValidationRules';
import { getDate } from '../../../composables/datetime';
import AssessmentActivation from '../../../api/assessments/manage/AssessmentActivation';


const props = defineProps({
    idempotency_key : {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    isFetchingActivationStatus : {
        type: Boolean,
        default: false,
    },
    action: {
        type: String,
        default: "create",
    },
    standalone: {
        type: Boolean,
        default: false,
    },

    minimumYear: {
        type: [String,Number],
        default: 2000,
    },

    yearOffset: {
        type: [String, Number],
        default: 1,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    },
    isFetchingYearsList : {
        type: Boolean,
        default: false,
    },
    yearsList: {
        type: Array,
        default: null,
    },
    activationStatusId: {
        type: String,
        default: null,
    },
    activationStatusModel: {
        type: Object,
        default: null,
    },
    onActivationEnabledChange: {
    type: Function,
    default: null,
},
});

defineExpose({
    validate,
    submit,
});

const currentYear = new Date().getFullYear()
const yearsList = ref(
    props?.yearsList
    ?? Array.from(
        Array(currentYear + Number(props.yearOffset)).keys()
    ).toReversed()
    .toSpliced(currentYear - Number(props.minimumYear) + Number(props.yearOffset) )
)
    
;
const localIsFetchingYearsList = ref(false);
const valid = ref(null);
const form = ref("form");
console.log(props);
const formValues = ref({
    type: props?.activationStatusModel?.type ?? null,
    year: props?.activationStatusModel?.year ?? null,
    enabled: props?.activationStatusModel?.enabled ?? null,
    starts_on: props?.activationStatusModel?.starts_on ?? null,
    ends_on:  props?.activationStatusModel?.ends_on ?? null, 
});

const submitError = ref(null);
const standaloneAlert = ref(null);

const localIsFectchingActivationStatus = ref(false);

const localAction = ref(props.action);
const localIsSubmitting = ref(false);

watch(
    () => formValues.value.enabled,
    (enabled) => {
        props.onActivationEnabledChange?.(enabled);
    },
    {
        immediate: true,
    },
);

const resolvedReadonlyFields = computed(() => {
    return props.readonly
            || actionReferences.isValidReadonlyAction(localAction.value)
})

const resolvedIsFetchingAssessmentActivationStatus = computed(() => {
    return props.isFetchingActivationStatus 
            || localIsFectchingActivationStatus.value;
});

const resolvedIsSubmitCallPending = computed(() => {
    return props.isSubmitting || localIsSubmitting.value;
})

const resolvedSubmitButtonLabel = computed(() => {
    const action = localAction.value;
    const pending = resolvedIsSubmitCallPending.value;
    let labelAction;
    switch (true) {
        case (
            actionReferences.isValidCreateAction(action)
            && formValues.value.enabled === null
        ):
            labelAction = `${pending ? 'Definindo' : 'Definir'} estado de `
            break;
        case (formValues.value.enabled === true):
            labelAction = `${pending ? 'Activando' : 'Activar'}`
            break;
        default: 
            labelAction = `${pending ? 'Bloqueando' : 'Bloquear'}`;
            break;
    }

    return `${labelAction} Avaliações${pending ? '...' : ''}`
})

const resolvedSubmitButtonIcon = computed(() => {
    const action = localAction.value;

    if(resolvedIsSubmitCallPending.value) {
        return "mdi-loading mdi-spin"
    }

    if(
        (
            actionReferences.isValidCreateAction(action)
            && formValues.value.enabled === null
        )
    ) {
        return "mdi-plus";
    }

    if(formValues.value.enabled === true) {
        return "mdi-lock-open-variant-outline";
    }
    return "mdi-lock-alert";
});

const resolvedSubmitButtonColor = computed(() => {
    const action = localAction.value;
    if(
        (
            actionReferences.isValidCreateAction(action)
            && formValues.value.enabled === null
        ) || formValues.value.enabled === true
    ) {
        return "success";
    }
    return "error";
    
})

const resolvedIsFetchingYearsList = computed(() => {
    return props.isFetchingYearsList || localIsFetchingYearsList.value;
}) 

async function validate() {
    const result = await form.value.validate();
    await nextTick();
    return result.valid;
    
}

async function submit() {
    if(resolvedIsSubmitCallPending.value ) {
        return;
    }

    try {
        submitError.value = null;
        standaloneAlert.value = null;
        await nextTick();

        if(actionReferences.isValidSubmitAction(localAction.value)) {
            const passedValidation = await validate();

            if(!passedValidation) {
                const errorTitle = "Erro de Validação";
                const errorMessage = "Ocorreu pelo menos um erro de validação. Verifique as mensagens de erro nos campos assinalados.";
                
                const firstError = form.value.errors[0].id;
                const targetInput = document.getElementById(firstError);

                targetInput.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                    container: "nearest",

                });
                targetInput.focus();

                if(props.standalone) {
                    submitError.value = {
                        title: errorTitle,
                        message: errorMessage,
                    }
                } else {
                    throw new Error(
                        errorMessage,
                        {cause: errorTitle},
                    )
                }

                return false;
            }
        }

        
        localIsSubmitting.value = true;
        const payload = {
            ...formValues.value,
            starts_on: getDate(formValues.value.starts_on),
            ends_on: getDate(formValues.value.ends_on)
        }
        const response = await AssessmentActivation.update(payload);

        if( props.standalone
            && (
                response?.status >= 200 
                && response?.status < 300
            
            )
        ) {
            standaloneAlert.value = {
                title: "Sucesso",
                message: `Avaliações ${formValues.value.enabled === true ? 'activadas' : 'bloqueadas'} com sucesso.`,
                color: formValues.value.enabled === false ? 'warning' : "success",
                type: formValues.value.enabled === false ? 'warning' : null,
                variant: formValues.value.enabled === false ? 'flat' : null
            };

            router.push({
                name: "assessments-manage"
            });
        }
        
    } catch (error) {
        if(props.standalone) {
            
        } else {
            // propagate / bubble up error when component
            // is not stand alone so the parent component is responsible
            // for error handling
            throw error;
        }
    } finally {
        localIsSubmitting.value = false;
    }
}

async function fetchAvailableYears() {
    if(
        resolvedIsFetchingYearsList.value
        || props?.yearsList?.length > 0
    ) {
        return;
    }

    try {
        localIsFetchingYearsList.value = true;
        const response = await AssessmentActivation.getAvailableYears();

        const newYearsList = response?.data?.available_years 
                            ?? response?.data
                            ?? [];

        if(newYearsList?.length > 0) {
            yearsList.value = [
                ...newYearsList?.toSorted()?.toReversed()
            ];
        }

    } catch (error) {
        
    } finally {
        localIsFetchingYearsList.value = false;
    }
}

async function fetchActivationStatusById(id) {
    if(resolvedIsFetchingAssessmentActivationStatus.value) {
        return;
    }

    try {
        localIsFectchingActivationStatus.value = true;

        const response = await AssessmentActivation.show(id);

        console.log(response?.data);
        
    } catch (error) {
        
    } finally {
        localIsFectchingActivationStatus.value = false;
    }
}

onMounted(() => {
    actionReferences.throwOnIllegalCrudAction(localAction.value);

    if(
        (
            props?.activationStatusId
            && !props?.activationStatusModel?.id
        ) || (
            props?.activationStatusModel?.id
            && (
                !props?.activationStatusModel?.type
                || [null, undefined].includes(props?.activationStatusModel?.enabled)
                || !props.activationStatusModel?.year
            )
        )
    ) {
        fetchActivationStatusById(
            props?.activationStatusModel?.id
            ?? props?.activationStatusId 
        )
    }

    fetchAvailableYears();
});
</script>